import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { nextTime } from "@/lib/constants";
import { StartPicker } from "@/components/StartPicker";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";

const OPTIONS = [{ h: 1, l: "1 hour" }, { h: 3, l: "An afternoon" }, { h: 4, l: "The evening" }];

export const QuickOuting = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const p = user?.profile || {};
  const [hours, setHours] = useState(3);
  const [start, setStart] = useState(p.home || null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const go = async () => {
    if (!start) return toast.error("Pick a starting point first.");
    setBusy(true);
    try {
      const { data } = await api.post("/plans/generate", {
        mode: "quick", start, start_time: nextTime(10), duration_hours: hours, budget: p.budget || "low",
        max_distance_km: Math.min(p.max_distance_km || 2, hours === 1 ? 1.5 : 3), transport: p.transport || "walk", group_type: "solo",
      });
      onOpenChange(false);
      nav(`/plans/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="z-[1250] sm:max-w-md" data-testid="quick-outing-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl"><Zap className="w-5 h-5 text-[#2F7D55]" /> I'm free now</DialogTitle>
            <DialogDescription>One tap, a ready-to-go outing that starts close by. Follow as much or as little as you like.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            {OPTIONS.map((o) => (
              <button key={o.h} data-testid={`quick-duration-${o.h}`} onClick={() => setHours(o.h)}
                className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${hours === o.h ? "bg-[#163A2B] text-white border-[#163A2B]" : "bg-white border-stone-200 hover:border-stone-400"}`}>
                {o.l}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-wider font-semibold text-stone-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Starting from</p>
            <StartPicker value={start} onChange={setStart} home={p.home} testPrefix="quick-start" />
          </div>
          <button data-testid="quick-go-btn" onClick={go} disabled={busy}
            className="w-full bg-[#2F7D55] hover:bg-[#23603F] text-white font-bold py-3 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-50">
            Get me out the door
          </button>
          <p className="text-xs text-stone-500 text-center">Uses your taste profile defaults. Your location is never shared.</p>
        </DialogContent>
      </Dialog>
      {busy && <GeneratingOverlay />}
    </>
  );
};
